import _ from 'lodash';
import * as WeMeetType from '@tencent/wemeet-js-sdk/build/types';
import i18n, { i18n_t } from '@/locales/i18n';
import { encodeText } from '@/renderer/scripts/utils/tools';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import { IS_DEV, IS_DEV_PUBLISH, IS_TEST, IS_PRIVATE } from '@/common/constant/platform';
import { CONSTANT_URL } from '@/renderer/scripts/http';

/**
 * 会议里应用只有线上应用和测试应用
 * 直接访问测试应用，对应前端和后端的test环境，不需要配代理，产品和测试可以直接体验
 */
// 会议空间才引入meeting包
const isTMWebview = window.navigator.userAgent.includes('app/tencent_wemeet') || false;
const wemeet = isTMWebview
  ? require('@tencent/wemeet-js-sdk')
  : {
      permission: {
        requestAuth: _.noop,
        requestAgentAuthCode: _.noop,
      },
      ui: {
        closeWebView: _.noop,
        changeWebAppPosition: _.noop,
        getWebAppPosition: _.noop,
      },
      app: {
        setShareOpenAppConfig: _.noop,
        shareOpenApp: _.noop,
      },
      event: {
        addEventListener: _.noop,
      },
    };
let prefix = '';
if (IS_DEV || IS_TEST) {
  prefix = 'test.';
}
// dev和test使用测试应用，pre和线上用正式应用
const isTestApp = IS_DEV || IS_DEV_PUBLISH || IS_TEST;
const sdkId = isTestApp ? '20940263730' : '18530189451';
const corpId = '1400115281';
const state = 'M7fzt2FP3';
// 此处不需要改造 protocol 协议，因为在腾讯会议中必然要用 https
const redirectURI = `https://${prefix}pan.tencent.com/web/meetingLogin`;
class Meeting {
  public SDK = wemeet;

  // 是否腾讯会议环境
  public isTMWebview = isTMWebview;
  public event = wemeet.event;

  // 会议里应用打开的窗口位置 弹窗=1；侧边栏=2；主视图内嵌=4；
  public wemeetAppPosition = 1;

  // 是否可用获取手机号 会议3.11.0版本开始支持
  public canUseGetPhoneNumber = false;

  // 跳转去授权
  public requestAuth = (loginToPath = '') => {
    /**
     * 需要保证loginToPath是一个完整的字符串，如果是/edit?name=xx&a=xx&b=cc,重定向后取不到完整的path
     * 这里先加密一次生成一个完整的字段，防止会议里回调url被转义，再手动encodeURIComponent两次
     * */
    const path = encodeText(loginToPath);
    const encodeTextPath = encodeURIComponent(encodeURIComponent(path));
    wemeet.permission.requestAuth({
      sdkId,
      corpId,
      redirectURI: loginToPath ? `${redirectURI}?login_path=${encodeTextPath}` : redirectURI,
      state,
    });
  };

  // 获取免登授权码
  public requestAgentAuthCode = (): Promise<any> =>
    new Promise((resolve, reject) => {
      wemeet.permission
        .requestAgentAuthCode({
          sdkId,
        })
        .then((res: any) => resolve(res))
        .catch((e: any) => reject(e));
    });

  // 关闭当前webview
  public closeWebview = () => {
    wemeet.ui.closeWebView();
  };
  public setShareOpenAppConfig = wemeet.app.setShareOpenAppConfig;
  public changeWebAppPosition = wemeet.ui.changeWebAppPosition;
  public getWebAppPosition = wemeet.ui.getWebAppPosition;
  public shareOpenApp = wemeet.app.shareOpenApp;
  public resetShare = () => {
    wemeet.app.setShareOpenAppConfig({
      shareEnable: true,
      pcUrl: CONSTANT_URL.appLoginUrl,
      mobileUrl: CONSTANT_URL.appLoginUrl,
    });
  };

  // 打开会议内邀请弹窗，用户点击确定或取消后触发回调
  public share = async (shareParams: WeMeetType.ShareOpenAppConfig, cb?: Function) => {
    const onShareFinished = (info: { code: number }) => {
      // "code": ""#分享结果，0:成功，1:失败
      const shareResult = info.code === 1 ? '0' : '1';
      cb && cb(shareResult);
      this.event.removeEventListener('open-app-shared', onShareFinished);
    };
    this.event.addEventListener('open-app-shared', onShareFinished);
    // 以下两个会议api在出错时不会进入catch 会议sdk1.0.41版本
    await wemeet.app.setShareOpenAppConfig(shareParams);
    wemeet.app.shareOpenApp();
  };

  // 比较版本 -1 小于给定版本, 0 等于给定版本, 1 大于给定版本
  private compareVersion = async (version: string) => {
    let result = -1;
    try {
      result = await wemeet.app.compare(version);
    } catch (e: any) {
      return -1;
    }
    return result;
  };

  // 检查是否可用自动获取手机号组件
  private checkIfCanUseGetPhoneNumber = async () => {
    const canUse = await this.compareVersion('3.11.0');
    this.canUseGetPhoneNumber = canUse !== -1;
  };

  // 设置窗口位置
  public setWemeetAppPosition = (position: number) => {
    this.wemeetAppPosition = position;
  };

  // 通过授权码获取签名
  private getSign = async () => {
    let sign: null | SMH.MeetingSign = null;
    try {
      const { authCode } = await this.requestAgentAuthCode();
      const url = window.location.href;
      const { data } = await smh.userAPI.getMeetingSign({
        code: authCode,
        url,
      });
      sign = data;
    } catch (e: any) {
      tips.error(i18n_t('获取会议签名失败'));
    }
    return sign;
  };

  // 获取到签名后去鉴权
  private agentConfig = async () => {
    const sign: unknown = await this.getSign();
    if (!sign) return false;
    try {
      await wemeet.permission.agentConfig(sign as SMH.MeetingSign);
      return true;
    } catch (e) {
      tips.error(i18n_t('会议鉴权失败，请重试'));
      return false;
    }
  };

  // 展示弹窗获取手机号权限
  public getPhoneNumber = async () => {
    tips.loading(i18n_t('正在获取手机号，请稍后'), 0);
    // 检查是否已经鉴权
    const { configState } = await wemeet.permission.checkJsApiConfig();
    if (configState === 0) {
      // 未鉴权 需要先鉴权
      const agentConfigDone = await this.agentConfig();
      if (!agentConfigDone) return;
    }
    return new Promise((resolve, reject) => {
      wemeet.data
        .getPhoneNumber({
          success: ({ code, message, token }: WeMeetType.GetPhoneNumberSuccessResp) => {
            tips.closeAll();
            resolve(token);
          },
          fail: ({ code, message }: WeMeetType.GetPhoneNumberResp) => {
            tips.closeAll();
            reject({
              code,
              message,
            });
          },
        })
        .catch((e: MsgError) => {
          tips.closeAll();
          reject(e);
        });
    });
  };
  constructor() {
    // 在会议环境下需要获取初始位置
    if (!IS_PRIVATE && this.isTMWebview) {
      this.getWebAppPosition().then((res: { position: number }) => {
        this.setWemeetAppPosition(res.position);
      });
      this.checkIfCanUseGetPhoneNumber();
    }
  }
}
export default new Meeting();
